/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Plan Pricing Grid & Token Packs Component (Task 10.6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Check,
  Sparkles,
  Zap,
  ArrowRight,
  CheckCircle2,
  Coins,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useSubscriptionStore, PlanItem } from '../../stores/useSubscriptionStore';
import { useUiStore } from '../../stores/useUiStore';

export const PlanPricingGrid: React.FC = () => {
  const { t } = useTranslation();
  const {
    plans,
    tokenPacks,
    subscription,
    billingInterval,
    setBillingInterval,
    changeSubscriptionPlan,
  } = useSubscriptionStore();

  const { openModal } = useUiStore();
  const [notification, setNotification] = useState<string | null>(null);

  const handleSwitchPlan = (plan: PlanItem) => {
    if (plan.id === subscription.planId) return;
    if (plan.monthlyPrice === 0) {
      changeSubscriptionPlan(plan.id);
      setNotification(t('subscription.switchSuccess', { plan: plan.name }));
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    const price = billingInterval === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
    openModal('payment_checkout', {
      itemType: 'subscription',
      itemId: plan.id,
      name: plan.name,
      credits: plan.creditsIncluded,
      amount: price,
      billingInterval,
      currency: 'USD',
    });
  };

  const handleBuyPack = (packId: string) => {
    const pack = tokenPacks.find((p) => p.id === packId);
    if (!pack) return;

    openModal('payment_checkout', {
      itemType: 'token_pack',
      itemId: pack.id,
      name: pack.name,
      credits: pack.credits,
      amount: pack.price,
      currency: 'USD',
    });
  };

  return (
    <div className="space-y-8">
      {/* Toast Notification */}
      {notification && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-medium animate-in fade-in slide-in-from-top-2 duration-200">
          <CheckCircle2 className="size-4 flex-shrink-0 text-emerald-500" />
          <span>{notification}</span>
        </div>
      )}

      {/* Interval Toggle */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold font-heading text-foreground">
            {t('subscription.title')}
          </h3>
          <p className="text-xs text-foreground-muted">
            {t('subscription.subtitle')}
          </p>
        </div>

        <div className="flex items-center p-1 rounded-xl bg-muted/60 border border-surface-border">
          <button
            type="button"
            onClick={() => setBillingInterval('monthly')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              billingInterval === 'monthly'
                ? 'bg-surface text-foreground shadow-xs'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            {t('subscription.monthly')}
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('yearly')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              billingInterval === 'yearly'
                ? 'bg-surface text-foreground shadow-xs'
                : 'text-foreground-muted hover:text-foreground'
            }`}
          >
            <span>{t('subscription.yearly')}</span>
            <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/20 text-primary uppercase">
              {t('subscription.saveDiscount')}
            </span>
          </button>
        </div>
      </div>

      {/* 3 Tier Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrent = plan.id === subscription.planId;
          const displayPrice = billingInterval === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;

          return (
            <div
              key={plan.id}
              className={`relative flex flex-col justify-between p-6 rounded-2xl bg-surface border transition-all ${
                plan.isFeatured
                  ? 'border-primary ring-2 ring-primary/20 shadow-md shadow-primary/5'
                  : 'border-surface-border hover:border-surface-border/80 shadow-xs'
              }`}
            >
              {plan.isFeatured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[11px] font-bold bg-primary text-primary-fg shadow-xs uppercase tracking-wide">
                  {t('subscription.popularBadge')}
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="text-lg font-bold font-heading text-foreground">
                    {plan.name}
                  </h4>
                  <p className="text-xs text-foreground-muted min-h-[32px]">
                    {plan.description}
                  </p>
                </div>

                {/* Price Display */}
                <div className="py-2 border-y border-surface-border space-y-0.5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold font-heading text-foreground">
                      ${displayPrice}
                    </span>
                    <span className="text-xs text-foreground-muted">
                      {billingInterval === 'yearly' ? t('subscription.perYear') : t('subscription.perMonth')}
                    </span>
                  </div>
                  {billingInterval === 'yearly' && plan.monthlyPrice > 0 && (
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                      {t('subscription.billedAnnually', { price: plan.yearlyPrice })}
                    </p>
                  )}
                  <p className="text-xs text-primary font-medium flex items-center gap-1 pt-1">
                    <Sparkles className="size-3" />
                    {t('subscription.creditsIncluded', { credits: plan.creditsIncluded.toLocaleString() })}
                  </p>
                </div>

                {/* Feature Checklist */}
                <ul className="space-y-2.5 pt-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-foreground">
                      <div className="size-4 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="size-2.5 stroke-[3]" />
                      </div>
                      <span className="leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Button */}
              <div className="pt-6 mt-6 border-t border-surface-border">
                {isCurrent ? (
                  <button
                    type="button"
                    disabled
                    className="w-full py-2.5 rounded-xl bg-muted text-foreground-muted text-xs font-semibold cursor-default text-center border border-surface-border"
                  >
                    {t('subscription.currentPlanBtn')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSwitchPlan(plan)}
                    className={`w-full py-2.5 rounded-xl text-xs font-semibold shadow-xs transition-all flex items-center justify-center gap-1.5 ${
                      plan.isFeatured
                        ? 'bg-primary hover:bg-primary-hover text-primary-fg shadow-sm hover:shadow-md active:scale-98'
                        : 'bg-surface hover:bg-surface-hover text-foreground border border-surface-border active:scale-98'
                    }`}
                  >
                    <span>
                      {plan.monthlyPrice === 0
                        ? t('subscription.freeBtn')
                        : plan.monthlyPrice > 49
                        ? t('subscription.upgradeBtn', { plan: plan.name })
                        : t('subscription.downgradeBtn', { plan: plan.name })}
                    </span>
                    <ArrowRight className="size-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* One-Time Credit Packs Section */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-primary/5 via-surface to-accent/5 border border-primary/20 space-y-4 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Coins className="size-4 text-primary" />
              <h4 className="text-base font-bold font-heading text-foreground">
                {t('subscription.boosterTitle')}
              </h4>
            </div>
            <p className="text-xs text-foreground-muted">
              {t('subscription.boosterSubtitle')}
            </p>
          </div>
          <span className="text-[11px] text-foreground-muted self-start sm:self-auto font-mono bg-surface px-2.5 py-1 rounded-lg border border-surface-border">
            100% Instant D1 Wallet Top-up
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
          {tokenPacks.map((pack) => (
            <div
              key={pack.id}
              className="flex flex-col justify-between p-4 rounded-xl bg-surface border border-surface-border hover:border-primary/40 transition-colors shadow-xs"
            >
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold font-heading text-foreground">
                    {pack.name}
                  </span>
                  {pack.badge && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-primary/10 text-primary">
                      {pack.badge}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold font-heading text-foreground">
                    ${pack.price}
                  </span>
                  <span className="text-[11px] text-foreground-muted">
                    ({pack.credits.toLocaleString()} credits)
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleBuyPack(pack.id)}
                className="mt-3 w-full py-2 rounded-lg bg-surface hover:bg-surface-hover text-foreground border border-surface-border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-98"
              >
                <Zap className="size-3.5 text-primary" />
                <span>{t('subscription.buyPackBtn', { credits: pack.credits.toLocaleString(), price: pack.price })}</span>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
